import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Search, User, Mail, Phone, Calendar, DollarSign, Eye, ChevronLeft, ChevronRight,
  Trash2, Download, X, Loader2, AlertTriangle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

const AdminCustomersPage = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Selection state
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Customer detail modal
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerBookings, setCustomerBookings] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('vipgez_admin_token');
    return { 'Authorization': `Bearer ${token}` };
  };

  useEffect(() => {
    const token = localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchCustomers();
  }, [navigate, page, search]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '20' });
      if (search) params.append('search', search);

      const response = await fetch(`${API_BASE}/api/admin/customers?${params}`, {
        headers: getAuthHeader()
      });
      const data = await response.json();

      if (data.success) {
        setCustomers(data.customers || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCustomers();
  };

  // Selection handlers
  const handleSelectCustomer = (email) => {
    setSelectedCustomers(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      }
      return [...prev, email];
    });
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === customers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map(c => c.email));
    }
  };

  // View customer detail
  const viewCustomerDetail = async (customer) => {
    setLoadingDetail(true);
    setShowDetailModal(true);
    setSelectedCustomer(customer);
    
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/customers/${encodeURIComponent(customer.email)}`,
        { headers: getAuthHeader() }
      );
      const data = await response.json();
      
      if (data.success) {
        setSelectedCustomer(data.customer);
        setCustomerBookings(data.bookings || []);
      }
    } catch (error) {
      console.error('Failed to fetch customer detail:', error);
      toast.error('Failed to load customer details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Delete customers
  const handleBulkDelete = async () => {
    if (selectedCustomers.length === 0) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/customers/bulk-delete`, {
        method: 'DELETE',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: selectedCustomers })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${data.deleted_count} customer(s) deleted`);
        setSelectedCustomers([]);
        setShowDeleteModal(false);
        fetchCustomers();
      } else {
        toast.error(data.detail || 'Failed to delete customers');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete customers');
    } finally {
      setIsDeleting(false);
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    try {
      toast.info('Preparing export...');
      
      // Fetch all customers for export
      const response = await fetch(`${API_BASE}/api/admin/customers/export`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Export completed');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export customers');
    }
  };

  // Calculate correct total spent from bookings
  const calculateTotalSpent = (customer) => {
    // Use paid_amount or final_total for accurate calculation
    return customer.total_spent || 0;
  };

  return (
    <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="customers-title">Customers</h1>
              <p className="text-muted-foreground">{total} customers</p>
            </div>
            <div className="flex gap-2">
              {selectedCustomers.length > 0 && (
                <Button 
                  onClick={() => setShowDeleteModal(true)} 
                  variant="destructive" 
                  size="sm"
                  data-testid="bulk-delete-customers-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete ({selectedCustomers.length})
                </Button>
              )}
              <Button onClick={exportToExcel} variant="outline" size="sm" data-testid="export-customers-btn">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button onClick={fetchCustomers} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Search */}
          <Card className="bg-secondary border-border mb-6">
            <CardContent className="pt-6">
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, or phone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="customer-search-input"
                  />
                </div>
                <Button type="submit" data-testid="customer-search-btn">Search</Button>
              </form>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-secondary border-border">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Customers</p>
                    <p className="text-2xl font-bold text-foreground">{total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Customers Table */}
          <Card className="bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Customer List</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : customers.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No customers found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-3 px-4 w-10">
                          <input
                            type="checkbox"
                            checked={selectedCustomers.length === customers.length && customers.length > 0}
                            onChange={handleSelectAll}
                            className="w-4 h-4 rounded border-border bg-muted cursor-pointer"
                            data-testid="select-all-customers"
                          />
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Customer</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Contact</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Bookings</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Total Spent</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Joined</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map((customer, idx) => (
                        <tr 
                          key={customer.email || idx} 
                          className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${
                            selectedCustomers.includes(customer.email) ? 'bg-muted/20' : ''
                          }`}
                        >
                          <td className="py-3 px-4">
                            <input
                              type="checkbox"
                              checked={selectedCustomers.includes(customer.email)}
                              onChange={() => handleSelectCustomer(customer.email)}
                              className="w-4 h-4 rounded border-border bg-muted cursor-pointer"
                              data-testid={`select-customer-${idx}`}
                            />
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="h-5 w-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-foreground">{customer.name || 'N/A'}</p>
                                <p className="text-xs text-muted-foreground">{customer.locale || 'en'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-sm text-foreground">
                                <Mail className="h-3 w-3 text-muted-foreground" />
                                <span className="truncate max-w-[200px]">{customer.email}</span>
                              </div>
                              {customer.phone && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Phone className="h-3 w-3" />
                                  <span>{customer.phone}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              {customer.total_bookings || 0} bookings
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1 text-sm font-medium text-foreground">
                              <DollarSign className="h-3 w-3 text-green-500" />
                              <span>€{(calculateTotalSpent(customer)).toFixed(2)}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewCustomerDetail(customer)}
                              data-testid={`view-customer-${idx}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages} ({total} customers)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Customer Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <Card className="bg-secondary border-border w-full max-w-3xl max-h-[90vh] overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-foreground">Customer Details</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowDetailModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[calc(90vh-100px)]">
              {loadingDetail ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : selectedCustomer && (
                <div className="space-y-6">
                  {/* Customer Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Name</p>
                      <p className="font-medium text-foreground">{selectedCustomer.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium text-foreground">{selectedCustomer.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium text-foreground">{selectedCustomer.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Bookings</p>
                      <p className="font-medium text-foreground">{selectedCustomer.total_bookings || customerBookings.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Spent</p>
                      <p className="font-medium text-green-500">
                        €{(customerBookings.reduce((sum, b) => sum + (b.paid_amount || b.final_total || b.total || 0), 0)).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Joined</p>
                      <p className="font-medium text-foreground">
                        {selectedCustomer.created_at ? new Date(selectedCustomer.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Bookings */}
                  <div>
                    <h3 className="text-md font-semibold text-foreground mb-3">Booking History</h3>
                    {customerBookings.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No bookings found</p>
                    ) : (
                      <div className="space-y-2">
                        {customerBookings.map((booking, idx) => (
                          <div 
                            key={booking.booking_ref || idx}
                            className="p-3 bg-muted/30 rounded-lg flex items-center justify-between"
                          >
                            <div>
                              <p className="font-mono text-sm text-primary">{booking.booking_ref}</p>
                              <p className="text-xs text-muted-foreground">
                                {booking.trip_date || booking.pickup_date} • {booking.pickup_location?.substring(0, 30)}...
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-foreground">
                                €{(booking.paid_amount || booking.final_total || booking.total || 0).toFixed(2)}
                              </p>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                booking.booking_status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                                booking.booking_status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                                'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {booking.booking_status || 'pending'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
          <Card className="bg-secondary border-border w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-lg text-foreground flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-400" />
                Delete Customers
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Are you sure you want to delete {selectedCustomers.length} customer(s)?
                This will NOT delete their bookings.
              </p>
              
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  This action cannot be undone.
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                  className="flex-1"
                  data-testid="confirm-delete-customers-btn"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete {selectedCustomers.length}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminCustomersPage;
